import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Grid,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Modal,
  Box,
  Typography,
  Snackbar,
  Card,
  Tooltip,
  Chip,
  CircularProgress,
} from "@mui/material";
import urlconfig from "../config/urlconfig";
import { getApiToken } from "../authUtils";
import { useMsal } from "@azure/msal-react";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PurchaseInvoiceModal from "../components/PurchaseInvoiceModal";
import { formatCurrency } from "../util";
import { DateRangePicker } from "react-date-range";
import { fetchCostSheet, useInvoiceDashboardData } from "./dashboardApi";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  height: "80%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 2,
  pb:0,
  overflowY: "auto",
  borderRadius: 2,
  overflowX:"hidden",
};

const PurchaseInvoiceDashboard = () => {
  const { items, refreshItems, deliveryItems, fetchAllItemsForDeliveryAnalytics } = useInvoiceDashboardData();
  const { instance, accounts } = useMsal();
  const account = accounts?.[0] || null;

  const [addPurchaseInvoice, setAddPurchaseInvoice] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const tableRef = useRef(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const [loading, setLoading] = useState(false);
  const [successToast, setSuccessToast] = useState(false);
  const [showMyOrders, setShowMyOrders] = useState(false);
  const [lastUploadedFiles, setLastUploadedFiles] = useState({});
  const [viewFileModal, setViewFileModal] = useState(false);
  const [viewFileUrl, setViewFileUrl] = useState(null);
  const [openDateModal, setOpenDateModal] = useState(false);
  const [dateRange, setDateRange] = useState([
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      ]);



  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchQuery(searchQuery), 400);
    return () => clearTimeout(handler);
  }, [searchQuery]);

// Fetch Last uplaoded files
useEffect(() => {
  const fetchLastUploadedFiles = async () => {
    if (!selectedItem?.id || !addPurchaseInvoice) return;

    try {
      const token = await getApiToken(instance, account);

      const response = await fetch(
        `${urlconfig.backend_api_base_url}/api/v2/bill/${selectedItem.id}/invoices?soNumber=${selectedItem.salesOrderNo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = await response.json();

      //Create a map: index -> file
      const fileMapByIndex = {};
      
      if (data?.files?.length && selectedItem.items?.length) {
        selectedItem.items.forEach((lineItem, idx) => {
          const matchingFiles = data.files.filter(file =>
              file.poNumber === lineItem.poNumber ||
              file.lineItemIndex === idx
            );
          // matching last files
          matchingFiles.forEach((file) => {
            if (!fileMapByIndex[idx]) fileMapByIndex[idx] = {};

            if (file.poBillUrl) {
              fileMapByIndex[idx].poBillUrl = file.poBillUrl;
            }

            if (file.invoiceFileUrl) {
              fileMapByIndex[idx].invoiceFileUrl = file.invoiceFileUrl;
            }

            if (file.purchaseInvoiceAmount) {
              fileMapByIndex[idx].purchaseInvoiceAmount = file.purchaseInvoiceAmount;
            }

            if (file.invoiceNo) {
              fileMapByIndex[idx].invoiceNo = file.invoiceNo;
            }
          });
         
          
        });
      }
      
      setLastUploadedFiles(fileMapByIndex);
    } catch (err) {
      console.error("Failed to fetch last uploaded files", err);
      setLastUploadedFiles({});
    }
  };

  fetchLastUploadedFiles();
}, [addPurchaseInvoice, selectedItem, instance, account]);

  //custom rows calculations
  const getCustomTotal = (rows, key) =>
  rows?.reduce((sum, r) => sum + (parseFloat(r[key]) || 0), 0) || 0;

const handleViewFile = (url) => {
  setViewFileUrl(url);
  setViewFileModal(true);
};

  const isClientFilteringActive =
    Boolean(debouncedSearchQuery) ||
    showMyOrders ||
    Boolean(dateRange[0].startDate);


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if(isClientFilteringActive){
            await refreshItems(debouncedSearchQuery, 0, 1000000);
        }else{
        await refreshItems(debouncedSearchQuery, page, rowsPerPage);
        }
      } catch (err) {
        console.error("Error refreshing items:", err);
      } finally {
        setLoading(false);
      }
    };


    fetchData();


  }, [debouncedSearchQuery, page, rowsPerPage, isClientFilteringActive]);

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
  setPage(0);
}, [isClientFilteringActive]);


  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const sendAuthenticatedRequest = useCallback(
    async (url, method, body, json = false) => {
      const accessToken = await getApiToken(instance, account);
      const headers = {
        Authorization: `Bearer ${accessToken}`,
      };
      if (json) headers["Content-Type"] = "application/json";


      const resp = await fetch(url, {
        method,
        headers,
        body: json ? JSON.stringify(body) : body,
      });

      if (!resp.ok) {
        let err = {};
        try {
          err = await resp.json();
        } catch (_) {
          err = { error: resp.statusText };
        }
        throw new Error(err.error || err.message || `HTTP ${resp.status}`);
      }
      const ct = resp.headers.get("content-type") || "";
      return ct.includes("application/json") ? resp.json() : resp.text();
    },
    [instance, account]


  );

  // ---- Save PO numbers ----
  const handleSavePoNumbers = async () => {
    if (!selectedItem || !selectedItem.id) return;


    if (!selectedItem.approvedBy || selectedItem.approvedBy.length === 0) {
      alert("PO Numbers can only be added to costsheets approved by at least one approver.");
      throw new Error("Not approved");
    }

    const safeItems = (selectedItem.items || []).map((si) => {
      const copy = { ...si };
      delete copy.poBillFile;
      delete copy.invoiceFile;
      delete copy.invoiceNo;
      delete copy.purchaseInvoiceAmount;
      return copy;
    });

    const payload = { ...selectedItem, items: safeItems };
    const url = `${urlconfig.backend_api_base_url}/api/v2/edit-costsheet/${selectedItem.id}`;
    await sendAuthenticatedRequest(url, "PUT", payload, true);
    await refreshItems();
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!selectedItem) return;
    setLoading(true);
    try {
      try { await handleSavePoNumbers(); } catch (err) {
        if (err.message === "Not approved") { setLoading(false); return; }
      }

      const now = new Date();
      const uploadTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 23);
      const token = await getApiToken(instance, account);

      const updatedFiles = [];

      for (let idx = 0; idx < selectedItem.items.length; idx++) {
        const sub = selectedItem.items[idx];
        if (!sub.poBillFile && !sub.invoiceFile && !sub.invoiceNo) continue;

        const formData = new FormData();
        if (sub.poBillFile) formData.append("poBillFile", sub.poBillFile);
        if (sub.invoiceFile) formData.append("invoiceFile", sub.invoiceFile);
        if (sub.invoiceNo) formData.append("invoiceNo", sub.invoiceNo);
        if (sub.poNumber) formData.append("poNumber", sub.poNumber);
        if (sub.purchaseInvoiceAmount) formData.append("purchaseInvoiceAmount", sub.purchaseInvoiceAmount);
        formData.append("billUploadedAt", uploadTime);

        const url = `${urlconfig.backend_api_base_url}/api/v2/bill/${selectedItem.id}/invoice/upload`;
        const res = await fetch(url, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (!res.ok) {
          let errorData = {};
          try { errorData = await res.json(); } catch (_) { errorData = { error: await res.text() }; }
          alert(`Upload failed for subitem #${idx + 1}: ${errorData.error || errorData}`);
          continue;
        }

        const data = await res.json();
        if (data.files) updatedFiles.push(...data.files);
      }

      setSelectedItem((prev) => ({ ...prev, files: updatedFiles.length ? updatedFiles : prev.files }));

      setSuccessToast(true)
      // setAddPurchaseInvoice(false);
      // setTimeout(()=>{
      //   setAddPurchaseInvoice(false)
      // },200)
      setAddPurchaseInvoice(false);
      await refreshItems();
    } catch (err) {
      alert(err.message || "Upload failed");
    } finally { setLoading(false); }
  };

const isInvoiceTouched = (it) =>
  it.invoiceNo || it.invoiceFile || it.purchaseInvoiceAmount;

const isValidInvoice = (it) =>
  it.invoiceNo?.trim() &&
  it.invoiceFile &&
  Number(it.purchaseInvoiceAmount) > 0;

const isValidPoOnly = (it) =>
  it.poNumber?.trim() && it.poBillFile;


  const isSubmitEnabled = selectedItem?.items?.length
  ? selectedItem.items.every((it) => {
      // If invoice is touched, it MUST be valid
      if (isInvoiceTouched(it)) {
        return isValidInvoice(it);
      }

      // Otherwise allow PO-only or empty row
      return !it.poBillFile || isValidPoOnly(it);
    }) &&
    selectedItem.items.some(
      (it) => isValidInvoice(it) || isValidPoOnly(it)
    )
  : false;

  const hasValidPoNumber = selectedItem?.items?.some((it) => it.poNumber && it.poNumber.trim() !== "");

   useEffect(()=>{
      fetchAllItemsForDeliveryAnalytics();
    },[]);

    const filterItemsByDate = (items, range=dateRange) => {
      const { startDate, endDate } = range[0];

      if (!startDate || !endDate) return items;

      return items.filter((item) => {
        if (!item.createdAT) return true;

        const createdAt = new Date(item.createdAT);
        return (
          createdAt >= new Date(startDate.setHours(0,0,0,0)) &&
          createdAt <= new Date(endDate.setHours(23,59,59,999))
        );
      });
    };

  const filteredPurchaseItems = React.useMemo(() => {
      if (!items?.content) return [];

      let data = [...items.content];

      if (debouncedSearchQuery) {
        const q = debouncedSearchQuery.toLowerCase();
        data = data.filter(
          (item) =>
            item.salesOrderNo?.toLowerCase().includes(q) ||
            item.createdBy?.toLowerCase().includes(q) ||
            item.items?.some((i) =>
              i.poNumber?.toLowerCase().includes(q)
            )
        );
      }

      if (showMyOrders) {
        const user = sessionStorage.getItem("username");
        data = data.filter((item) => item.createdBy === user);
      }

      data = filterItemsByDate(data);

      return data;
    }, [items, debouncedSearchQuery, showMyOrders, dateRange]);

    const finalItems = useMemo(() => {
      if (isClientFilteringActive) {
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;
        return filteredPurchaseItems.slice(start, end);
      }

      return items?.content || [];
    }, [items, filteredPurchaseItems, page, rowsPerPage, isClientFilteringActive]);

    const paginationCount = isClientFilteringActive
      ? filteredPurchaseItems.length
      : items?.totalElements || 0;

    const filterCardValues = useMemo(()=>{
       if (!deliveryItems?.content) return [];

      let data = [...deliveryItems.content];

      if (debouncedSearchQuery) {
        const q = debouncedSearchQuery.toLowerCase();
        data = data.filter(
          (item) =>
            item.salesOrderNo?.toLowerCase().includes(q) ||
            item.createdBy?.toLowerCase().includes(q) ||
            item.items?.some((i) =>
              i.poNumber?.toLowerCase().includes(q)
            )
        );
      }

      if (showMyOrders) {
        const user = sessionStorage.getItem("username");
        data = data.filter((item) => item.createdBy === user);
      }

      data = filterItemsByDate(data);

      return data;
    }, [deliveryItems, debouncedSearchQuery, showMyOrders, dateRange]);
    

  const totalPurchaseOrderValue = React.useMemo(()=>{
    if (!deliveryItems?.content) return 0;
   const total = filterCardValues.reduce((sum, item)=>{
    const itemsTotal =
      item.items?.reduce((s, i) => {
        const pp = Number(i.pp || 0);
        const gst = Number(i.gst || 0);
        const qty = Number(i.qty || 0);

        const unitCostWithGst = pp + (pp * gst) / 100;
        return s + unitCostWithGst * qty;
      }, 0) || 0;

     const transportPp = Number(item.transportationTotalPp || 0);
     const transportGst = Number(item.costSummary?.transportationGST || 0);

     const customizationPp = Number(item.customizationTotalPp || 0);
     const customizationGst = Number(item.costSummary?.customizationGST || 0);

     const marketingPp = Number(item.marketingTotalPp || 0);
     const marketingGst = Number(item.costSummary?.marketingGST || 0);

     const customRowsTotal = (item.customRows?.reduce((s, row) => {
       const pp = Number(row.customTotalPp || 0);
       const gst = Number(row.customGST || 0);
       return s + pp + (pp * gst) / 100;
     }, 0) || 0);

      const poValue =
        itemsTotal +
        (transportPp + (transportPp * transportGst) / 100) +
        (customizationPp + (customizationPp * customizationGst) / 100) +
        (marketingPp + (marketingPp * marketingGst) / 100) +
        customRowsTotal;
      return sum + poValue;
    }, 0) || 0;
    return total;
  }, [filterCardValues])

  const totalPurchaseOrderValueByItem = React.useMemo(() => {
  if (!selectedItem) return 0;

  const itemsTotal =
    selectedItem.items?.reduce(
      (sum, i) => sum + Number(i.totalPp || 0),
      0
    ) || 0;
    const transportPp = Number(selectedItem.transportationTotalPp || 0);
    const transportGst = Number(selectedItem.costSummary?.transportationGST || 0);

    const customizationPp = Number(selectedItem.customizationTotalPp || 0);
    const customizationGst = Number(selectedItem.costSummary?.customizationGST || 0);

    const marketingPp = Number(selectedItem.marketingTotalPp || 0);
    const marketingGst = Number(selectedItem.costSummary?.marketingGST || 0);

    const customRowsTotal = (selectedItem.customRows?.reduce((s, row) => {
      const pp = Number(row.customTotalPp || 0);
      const gst = Number(row.customGST || 0);
      return s + pp + (pp * gst) / 100;
    }, 0) || 0);
  
    return itemsTotal +
      (transportPp + (transportPp * transportGst) / 100) +
      (customizationPp + (customizationPp * customizationGst) / 100) +
      (marketingPp + (marketingPp * marketingGst) / 100) +
      customRowsTotal;
}, [selectedItem]);

  const totalPurchaseInvoiceValue = React.useMemo(() => {
  if (!filterCardValues?.length) return 0;

  return filterCardValues.reduce((sum, item) => {
    const invoiceTotal =
      (item.purchaseInvoiceBillsList || []).reduce(
        (s, f) => s + (Number(f?.purchaseInvoiceAmount) || 0),
        0
      );

    const transportationTotal =
      (item.transportationDetails || []).reduce(
        (s, t) => s + (Number(t?.actualTransportationCost) || 0),
        0
      );

    const customizationTotal =
      (item.customizationDetails || []).reduce(
        (s, c) => s + (Number(c?.actualCustomizationCost) || 0),
        0
      );

    return sum + invoiceTotal + transportationTotal + customizationTotal;
  }, 0);
}, [filterCardValues]);

//By selected item
const totalPurchaseInvoiceValueByItem = React.useMemo(() => {
  if (!selectedItem) return 0;

  const invoiceTotal =
    selectedItem.purchaseInvoiceBillsList?.reduce(
      (sum, bill) => sum + Number(bill.purchaseInvoiceAmount || 0),
      0
    ) || 0;

  const transportationTotal =
    selectedItem.transportationDetails?.reduce(
      (sum, t) => sum + Number(t.actualTransportationCost || 0),
      0
    ) || 0;

  const customizationTotal =
    selectedItem.customizationDetails?.reduce(
      (sum, c) => sum + Number(c.actualCustomizationCost || 0),
      0
    ) || 0;

  return invoiceTotal + transportationTotal + customizationTotal;
}, [selectedItem]);

  const balanceValue = totalPurchaseOrderValue - totalPurchaseInvoiceValue;

  const balanceValueByitem = totalPurchaseOrderValueByItem - totalPurchaseInvoiceValueByItem

  return (
    <>
      <h1>Purchase Dashboard</h1>

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Search by Sales Order No, Created By....."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchQuery("")} size="small">
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>
      <Box>
              <Grid container alignItems="center" justifyContent="space-between" gap={1} sx={{ p: 1}}>
                <Grid item md={6}>
                  <Typography variant="h6">Filter By Date & Created By</Typography>
                </Grid>
                
                <Grid item md={5} sx={{ textAlign: "right", }} >
                  <Button
                        variant="outlined"
                        onClick={() => setOpenDateModal(true)} 
                        sx={{margin:"5px"}}
                      >
                        {!dateRange[0].startDate
                          ? "Filter by Date Range"
                          : `${dateRange[0].startDate.toLocaleDateString()} - ${dateRange[0].endDate.toLocaleDateString()}`}
                      </Button>

                      {dateRange[0].startDate && (
                        <Chip
                          label="Clear"
                          onDelete={() =>
                            setDateRange([{ startDate: null, endDate: null, key: "selection" }])
                          }
                          sx={{ ml: 1 }}
                        />
                      )}

                      <Modal open={openDateModal} onClose={() => setOpenDateModal(false)}>
                      <Box  sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          bgcolor: "background.paper",
                          p: 2,
                          borderRadius: 2,
                          boxShadow: 24,
                          width: "fit-content",
                        }}>
                            <DateRangePicker
                            ranges={dateRange}
                            onChange={(item) => setDateRange([item.selection])}
                            />
                            <Box sx={{ textAlign: "right", mt: 2 }}>
                              <Button
                              onClick={() => {
                                setDateRange([{ startDate: null, endDate: null, key: "selection" }]);
                                setOpenDateModal(false);
                              }}
                            >
                              Clear
                            </Button>
                            <Button onClick={() => setOpenDateModal(false)}>Apply</Button>
                            </Box>
                        </Box>
                        </Modal>
                      
                    <Button
                          variant="outlined"
                          onClick={() => setShowMyOrders(prev => !prev)}
                          
                        >
                          {showMyOrders
                            ? `${sessionStorage.getItem("username")} Orders`
                            : "Show My Orders"}
                        </Button>

                        {showMyOrders && (
                          <Chip
                            label="Clear Filter"
                            onDelete={() => setShowMyOrders(false)}
                            sx={{ ml: 1 }}
                          />
                        )}
                </Grid>
              </Grid>
      </Box>

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2, p:1 }} justifyContent="center">
        <Grid item md={4}>
          <Card sx={{ p: 2, boxShadow: 3,mt: 0, backgroundColor: "#1E40AF", color:"#fff" }}>              
          <Typography variant="h2" fontWeight={"bold"} color={"#fff"} textAlign={"center"} mt={2}>
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              <span> ₹{formatCurrency(totalPurchaseOrderValue)}</span>
            )}
          </Typography>
          <Typography variant="h6" component="h2" sx={{ fontWeight: "bold", fontSize: "18px", color: "#ffffff", textAlign: "center" }}>
            Purchase Order Values
            </Typography>
            </Card>

        </Grid>
        <Grid item md={4}>
          <Card sx={{ p: 2, boxShadow: 3, mt: 0, backgroundColor: "#047857", color:"#fff" }}>              
          <Typography variant="h2" fontWeight={"bold"} color={"#fff"} textAlign={"center"} mt={2}>
          {loading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
             <span> ₹{formatCurrency(totalPurchaseInvoiceValue)}</span>
          )}
          </Typography>
          <Typography variant="h6" component="h2" sx={{ fontWeight: "bold", fontSize: "18px", color: "#ffffff", textAlign: "center" }}>
            Purchase Invoice Values
            </Typography>
            </Card>
        </Grid>
        <Grid item md={4}> 
            <Card sx={{ p: 2, boxShadow: 3, mt: 0, backgroundColor: "#B91C1C", color:"#fff" }}>              
          <Typography variant="h2" fontWeight={"bold"} color={"#fff"} textAlign={"center"} mt={2}>
              {loading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                <span> ₹{formatCurrency(balanceValue)}</span>
              )}  
          </Typography>
          <Typography variant="h6" component="h2" sx={{ fontWeight: "bold", fontSize: "18px", color: "#ffffff", textAlign: "center" }}>
            Balance Values
            </Typography>
            </Card>
        </Grid>
      </Grid>

      {/* TABLE */}
      <TableContainer component={Paper} elevation={1} sx={{ overflowX: "auto", mb: 4, maxHeight: "100vh" }}>
        <Table ref={tableRef} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Creation Date</TableCell>
              <TableCell sx={{width:"150px"}}>SO No. <br/> Created By</TableCell>
              <TableCell>PO No.</TableCell>
              <TableCell>PO Value</TableCell>
              <TableCell>PI Value</TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

              <TableBody>
    {finalItems.map((item)=>{
      const transportPp = Number(item.transportationTotalPp || 0);
      const transportGst = Number(item.costSummary?.transportationGST || 0);

      const customizationPp = Number(item.customizationTotalPp || 0);
      const customizationGst = Number(item.costSummary?.customizationGST || 0);

      const marketingPp = Number(item.marketingTotalPp || 0);
      const marketingGst = Number(item.costSummary?.marketingGST || 0);
    const poValue =
      (item.items?.reduce((sum, i) => {
        const pp = Number(i.pp || i.totalPp || 0);
        const gst = Number(i.gst || 0);
        const qty = Number(i.qty || 0);

        const ppWithGst = pp + (pp * gst) / 100;
        return sum + ppWithGst * qty;
      }, 0) || 0) +
      (transportPp + (transportPp * transportGst) / 100) +
      (customizationPp + (customizationPp * customizationGst) / 100) +
      (marketingPp + (marketingPp * marketingGst) / 100) +
      (item.customRows?.reduce((s, row) => {
        const pp = Number(row.customTotalPp || 0);
        const gst = Number(row.customGST || 0);
        return s + pp + (pp * gst) / 100;
      }, 0) || 0);

    const totalPIItem = (item.purchaseInvoiceBillsList || []).reduce(
      (sum, f) => sum + Number(f.purchaseInvoiceAmount || 0),
      0
    );
    const transportationItem = (item.transportationDetails?.reduce(
      (sum, t) => sum + Number(t.actualTransportationCost || 0),
      0))

      const customizationItem = (item.customizationDetails?.reduce(
        (sum, c)=>sum + Number(c.actualCustomizationCost || 0), 0
      ))

      const totalPI = totalPIItem + transportationItem +customizationItem


    const balance = poValue - totalPI;

    return (
      <TableRow key={item.id}>
        <TableCell>{item.createdAT ? new Date(item.createdAT).toLocaleDateString() : "N/A"}</TableCell>
        <TableCell sx={{maxWidth:"200px", width:"200px", wordWrap:"break-word"}}>{item.salesOrderNo}<br />{item.createdBy}</TableCell>
        <TableCell>{item.items.map((i) => i.poNumber).filter(Boolean).join(", ")}</TableCell>
        <TableCell>₹{formatCurrency(poValue)}
           <Tooltip title="Includes transportation and customization charges" arrow>
             
            </Tooltip>
        </TableCell>
        <TableCell>₹{formatCurrency(totalPI)}</TableCell>
        <TableCell>₹{formatCurrency(balance)}</TableCell>
        <TableCell>
          <Button
            variant="outlined"
            sx={{ fontSize: "14px", my: "12px", width: "100%" }}
            // onClick={() => { setSelectedItem(item); setAddPurchaseInvoice(true); }}
             onClick={async () => {
                // The dialog edits the whole cost sheet; while filtering, rows carry only what the table shows
                let costSheet;
                try {
                  costSheet = await fetchCostSheet(instance, account, item.id);
                } catch (err) {
                  alert("Failed to load cost sheet: " + err.message);
                  return;
                }
                const itemsWithLock = costSheet.items.map((i) => ({
                  ...i,
                  _locked: Boolean(i.poNumber), // 🔒 lock only DB values
                }));

                setSelectedItem({ ...costSheet, items: itemsWithLock });
                setAddPurchaseInvoice(true);
              }}
          >
            Add PO & PI
          </Button>
          <PurchaseInvoiceModal row={item} getApiToken={() => getApiToken(instance, account)} />
        </TableCell>
      </TableRow>
    );
  })}
</TableBody>

        </Table>

        <TablePagination
          component="div"
          count={paginationCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* MODAL */}
      <Modal open={addPurchaseInvoice} onClose={() => setAddPurchaseInvoice(false)}>
        <Box sx={modalStyle}>
          <IconButton onClick={() => setAddPurchaseInvoice(false)} sx={{ position: "absolute", top: 8, right: 8 }}>
            <CloseIcon />
          </IconButton>

          {selectedItem && (
            <>
              <Typography variant="h4" fontWeight="600" mb={2}>
                Add PO & PI :- {selectedItem.salesOrderNo}
              </Typography>

              {/* SubItems Table */}
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vendor Name</TableCell>
                    <TableCell>Detail</TableCell>
                    <TableCell>Purchase Order No.</TableCell>
                    <TableCell>Upload Purchase Order</TableCell>
                    <TableCell>PO Value</TableCell>
                    <TableCell>Purchase Invoice No.</TableCell>
                    <TableCell>Upload Purchase Invoice</TableCell>
                    <TableCell>Purchase Invoice Value</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {selectedItem.items.map((i, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{i.vendorName}</TableCell>
                      <TableCell>{i.details}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={i.poNumber ?? ""}
                          InputProps={{
                                readOnly: i._locked || false,
                                sx: { backgroundColor: i._locked ? "#f0f0f0" : "inherit"},
                            }}
                          placeholder="Enter PO Number"
                          onChange={(e) => {
                              if (i._locked) return;
                            const updated = [...selectedItem.items];
                            updated[idx] = { ...updated[idx], poNumber: e.target.value };
                            setSelectedItem({ ...selectedItem, items: updated });
                          }}
                        />
                        {i._locked && (
                          <Typography variant="caption" sx={{ color: "red" }}>
                            PO Number is locked and cannot be changed.
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          disabled={!i.poNumber || i.poNumber.trim() === ""}
                        >
                          Upload PO
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            hidden
                            onChange={(e) => {
                              const file = e.target.files[0];
                              const updated = [...selectedItem.items];
                              updated[idx] = { ...updated[idx], poBillFile: file };
                              setSelectedItem({ ...selectedItem, items: updated });
                            }}
                          />
                        </Button>

                        {i.poBillFile && (
                          <Typography variant="caption" sx={{ color: "green" }}>
                            {i.poBillFile.name} (PO: {i.poNumber})
                          </Typography>
                        )}

                        {(() => {
                      const lastFile = lastUploadedFiles[idx];
                      return lastFile?.poBillUrl ? (
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label="View Last PO"
                            color="primary"
                            variant="outlined"
                            size="small"
                            onClick={() => handleViewFile(lastFile.poBillUrl)}
                          />
                        </Box>
                      ) : null;
                    })()}
                        
                      </TableCell>
                      <TableCell>
                        {/**total PP incl gst */}                          
                          {(() => {
                            const pp = Number(i.pp || 0);
                            const gst = Number(i.gst || 0);
                            const qty = Number(i.qty || 0);
                            return formatCurrency((pp + (pp * gst) / 100) * qty);
                          })()}
                      </TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={i.invoiceNo || ""}
                          placeholder="Enter Invoice Number"
                          onChange={(e) => {
                            const updated = [...selectedItem.items];
                            updated[idx] = { ...updated[idx], invoiceNo: e.target.value };
                            setSelectedItem({ ...selectedItem, items: updated });
                          }}
                        />
                        {(() => {
                          const lastFile = lastUploadedFiles[idx];
                          return lastFile?.invoiceNo ? (
                            <Chip
                              label={`Last PI No: ${lastFile.invoiceNo}`}
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          ) : null;
                        })()}
                      </TableCell>

                      <TableCell>
                        <Button variant="outlined" component="label" fullWidth>
                          Upload PI
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            hidden
                            onChange={(e) => {
                              const file = e.target.files[0];
                              const updated = [...selectedItem.items];
                              updated[idx] = { ...updated[idx], invoiceFile: file };
                              setSelectedItem({ ...selectedItem, items: updated });
                            }}
                          />
                        </Button>

                        {i.invoiceFile && (
                          <Typography variant="caption" sx={{ color: "blue" }}>
                            {i.invoiceFile.name}
                          </Typography>
                        )}


                        {(() => {
                            const lastFile = lastUploadedFiles[idx]; // ✅ Use index
                            return lastFile?.invoiceFileUrl ? (
                              <Box sx={{ mt: 1 }}>
                                <Chip
                                  label="View Last PI"
                                  color="success"
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleViewFile(lastFile.invoiceFileUrl)}
                                />
                              </Box>
                            ) : null;
                          })()}
                      </TableCell>

                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          type="text"
                          value={i.purchaseInvoiceAmount || ""}
                          inputProps={{
                              inputMode: "numeric",
                              pattern: "[0-9.]*"
                            }}
                            onKeyDown={(e) => {
                              if (
                                !/[0-9.]/.test(e.key) &&
                                ![
                                  "Backspace",
                                  "Delete",
                                  "ArrowLeft",
                                  "ArrowRight",
                                  "Tab"
                                ].includes(e.key)
                              ) {
                                e.preventDefault();
                              }
                              if (e.key === "." && e.target.value.includes(".")) {
                                e.preventDefault();
                              }
                            }}
                            onPaste={(e) => {
                              const paste = e.clipboardData.getData("text");
                              if (!/^\d*\.?\d*$/.test(paste)) {
                                e.preventDefault();
                              }
                            }}
                          placeholder="Enter Invoice Amount"
                          onChange={(e) => {
                             const value = e.target.value; 
                              if (!/^\d*\.?\d*$/.test(value)) return;
                            const updated = [...selectedItem.items];
                            updated[idx] = { ...updated[idx], purchaseInvoiceAmount: e.target.value };
                            setSelectedItem({ ...selectedItem, items: updated });
                          }}
                        />
                        {(() => {
                          const lastFile = lastUploadedFiles[idx];
                          return lastFile?.purchaseInvoiceAmount ? (
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={`Last PI Amount: ₹${formatCurrency(lastFile.purchaseInvoiceAmount)}`}
                                color="secondary"
                                variant="outlined"
                                size="small"
                              />
                            </Box>
                          ) : null;
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmit}
                  disabled={loading || !isSubmitEnabled || !hasValidPoNumber}
                >
                  {loading ? "Processing..." : "Submit"}
                </Button>
              </Grid>
            </>
          )}
          <Grid container my={2}>
            <Grid item md={6}>
                    <Box
                      sx={{
                        mt: 2,
                        mx:2,
                        p: 2,
                        borderRadius: 3,
                        background: "linear-gradient(135deg, #f5f7fa, #e4e7eb)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold" mb={2}>
                        Transportation Cost Summary
                      </Typography>

                      <Grid container spacing={2}>
                        {/* Budgeted */}
                        <Grid item xs={12} sm={4}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              backgroundColor: "#ffffff",
                              textAlign: "center",
                              height:"75px"
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Budgeted Transportation Cost
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                              {(() => {
                                const pp = Number(selectedItem?.transportationTotalPp || 0);
                                const gst = Number(selectedItem?.costSummary?.transportationGST || 0);
                                return `₹${formatCurrency(pp + (pp * gst) / 100)}`;
                              })()}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Actual */}
                        <Grid item xs={12} sm={4}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              backgroundColor: "#ffffff",
                              textAlign: "center",
                              height:"75px"
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Actual Transportation Cost
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                              ₹{formatCurrency(
                                selectedItem?.transportationDetails?.reduce(
                                  (sum, item) => sum + Number(item.actualTransportationCost || 0),
                                  0
                                ) || 0
                              )}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Variance */}
                        <Grid item xs={12} sm={4}>
                          {(() => {
                            const tPp = Number(selectedItem?.transportationTotalPp || 0);
                            const tGst = Number(selectedItem?.costSummary?.transportationGST || 0);
                            const budget = tPp * (1 + tGst / 100);
                            // const budget = Number(selectedItem?.transportationTotalPp || 0);
                            const actual =
                              selectedItem?.transportationDetails?.reduce(
                                (sum, item) => sum + Number(item.actualTransportationCost || 0),
                                0
                              ) || 0;
                            const variance = budget - actual;

                            return (
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  backgroundColor: variance >= 0 ? "#e8f5e9" : "#fdecea",
                                  textAlign: "center",
                                  height:"75px"
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  Variance
                                </Typography>
                                <Typography
                                  variant="h6"
                                  fontWeight="bold"
                                  color={variance >= 0 ? "success.main" : "error.main"}
                                >
                                  ₹{formatCurrency(variance)}
                                </Typography>
                              </Box>
                            );
                          })()}
                        </Grid>
                      </Grid>
                    </Box>
              </Grid>



            <Grid item md={6}>
              <Box
                sx={{
                  mt: 2,
                  mx:2,
                  p: 2,
                  borderRadius: 3,
                  background: "linear-gradient(135deg, #f5f7fa, #e4e7eb)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Customization Cost Summary
                </Typography>

                <Grid container spacing={2}>
                  {/* Budgeted */}
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "#ffffff",
                        textAlign: "center",
                        height:"75px"
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Budgeted Customization Cost
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {(() => {
                          const pp = Number(selectedItem?.customizationTotalPp || 0);
                          const gst = Number(selectedItem?.costSummary?.customizationGST || 0);
                          return `₹${formatCurrency(pp + (pp * gst) / 100)}`;
                        })()}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Actual */}
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "#ffffff",
                        textAlign: "center",
                        height:"75px"
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Actual Customization Cost
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        ₹{formatCurrency(
                          selectedItem?.customizationDetails?.reduce(
                            (sum, item) => sum + Number(item.actualCustomizationCost || 0),
                            0
                          ) || 0
                        )}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Variance */}
                  <Grid item xs={12} sm={4}>
                    {(() => {
                      const cPp = Number(selectedItem?.customizationTotalPp || 0);
                      const cGst = Number(selectedItem?.costSummary?.customizationGST || 0);
                      const budget = cPp * (1 + cGst / 100);
                      // const budget = Number(selectedItem?.customizationTotalPp || 0);
                      const actual =
                        selectedItem?.customizationDetails?.reduce(
                          (sum, item) => sum + Number(item.actualCustomizationCost || 0),
                          0
                        ) || 0;
                      const variance = budget - actual;

                      return (
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: variance >= 0 ? "#e8f5e9" : "#fdecea",
                            textAlign: "center",
                            height:"75px"
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Variance
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color={variance >= 0 ? "success.main" : "error.main"}
                          >
                            ₹{formatCurrency(variance)}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>

            
            <Box sx={{
                position: "sticky",
                maxHeight:"100px",
                bottom: 0,
                left: 0,
                width: "100%",
                bgcolor: "#fff",
                zIndex: 10,
                p: 1.5,
                margin:"auto",
                boxShadow: "0px -2px 10px rgba(0,0,0,0.2)",
              }}>
            <Grid container justifyContent={"space-between"} >
              <Grid item alignContent="center">
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  Total Purchase Order Value: ₹{formatCurrency(totalPurchaseOrderValueByItem)}
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  Total Purchase Invoice Value: ₹{formatCurrency(totalPurchaseInvoiceValueByItem)}
                </Typography>
              </Grid>
               <Grid item>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  Balance: ₹{formatCurrency(balanceValueByitem)}
                </Typography>
              </Grid>
              </Grid>
            </Box>            
        </Box>
      </Modal>

    <Modal
        open={viewFileModal}
        onClose={() => setViewFileModal(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            height: "85%",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1,
              borderBottom: "1px solid #eee",
            }}
          >
            <Typography variant="h6">
              PDF Preview
            </Typography>

            <IconButton onClick={() => setViewFileModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* PDF Container */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 2,
            }}
          >
            {viewFileUrl && (
              <iframe
                src={`${viewFileUrl}#toolbar=1`}
                title="PDF Viewer"
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  borderRadius: "8px",
                }}
              />
            )}
          </Box>
        </Box>
      </Modal>


      {successToast && (
            <Snackbar
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              open={successToast}
              autoHideDuration={3000}
              onClose={() => setSuccessToast(false)}
              message="Purchase details has been added successfully."
              action={
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => setSuccessToast(false)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            />
          )}
    </>


  );
};

export default PurchaseInvoiceDashboard;
